/**
 * @fileoverview Sistema Modal TypeScript - Plugin Vue per modal dinamici
 *
 * Sistema completo per creare e gestire modal dinamici in modo programmatico.
 * Supporta qualsiasi componente Vue come contenuto, gestione loading/validation,
 * e API moderna type-safe mantenendo retrocompatibilità.
 *
 * @author Generated by TypeScript Migration
 * @since 2025-09-25
 */

import Vue from 'vue';
import type { VueConstructor, PluginFunction } from 'vue';
import Modal from './Modal.vue';

/**
 * Callback data structure per modal
 */
export interface ModalCallbackData {
	/** Istanza del modal component */
	instance: any;
	/** Dati raccolti dal form interno */
	data: any;
}

/**
 * Callback function type per azioni modal
 */
export type ModalCallback = (callbackData: ModalCallbackData) => void;

/**
 * Props per la creazione di un modal
 */
export interface ModalProps {
	/** Titolo del modal */
	title: string;
	/** Componente Vue da renderizzare nel body */
	body: any; // Vue Component type
	/** Props da passare al componente body */
	bodyProps?: Record<string, any>;
	/** Callback per azione conferma */
	onConfirm: ModalCallback;
	/** Callback per azione cancellazione */
	onCancel?: ModalCallback;
	/** Mostra bottone conferma */
	confirmButton?: boolean;
	/** Testo bottone cancellazione */
	cancelButtonText?: string;
}

/**
 * Interfaccia per l'istanza modal restituita
 */
export interface ModalInstance {
	/** Chiude il modal */
	close: () => void;
	/** Avvia loading spinner */
	startLoading: () => void;
	/** Ferma loading spinner */
	stopLoading: () => void;
	/** Ottiene i dati raccolti dal form */
	getData: () => any;
}

/**
 * Interfaccia per il servizio modal programmatico
 */
export interface ModalProgrammaticService {
	/**
	 * Apre un nuovo modal con il componente specificato
	 *
	 * @param title - Titolo del modal
	 * @param body - Componente Vue da renderizzare
	 * @param bodyProps - Props per il componente
	 * @param onConfirm - Callback per conferma
	 * @param onCancel - Callback per cancellazione
	 * @param confirmButton - Mostra bottone conferma
	 * @param cancelButtonText - Testo bottone cancellazione
	 * @returns Istanza modal con metodi di controllo
	 */
	open(
		title: string,
		body: any, // Vue Component
		bodyProps?: Record<string, any>,
		onConfirm?: ModalCallback,
		onCancel?: ModalCallback,
		confirmButton?: boolean,
		cancelButtonText?: string,
	): ModalInstance;
}

/**
 * Crea una nuova istanza di modal e la appende al DOM
 *
 * Utilizza il pattern Factory per creare istanze Vue dinamiche del componente Modal,
 * permettendo l'apertura programmatica di dialog senza dover gestire template.
 *
 * @param propsData - Props per il componente Modal
 * @returns Istanza modal con metodi di controllo
 *
 * @example
 * ```typescript
 * const modalInstance = modal({
 *   title: 'Conferma operazione',
 *   body: MyFormComponent,
 *   bodyProps: { userId: 123 },
 *   onConfirm: (data) => console.log('Confermato:', data),
 * });
 *
 * // Controllo programmatico
 * modalInstance.startLoading();
 * modalInstance.stopLoading();
 * modalInstance.close();
 * ```
 */
function modal(propsData: ModalProps): ModalInstance {
	try {
		// Extend del componente Modal
		const ModalConstructor = Vue.extend(Modal) as VueConstructor<Vue>;

		// Crea elemento DOM container
		const appElement = document.getElementById('app');
		if (!appElement) {
			throw new Error('Element #app not found in DOM');
		}

		const containerElement = document.createElement('div');
		appElement.appendChild(containerElement);

		// Crea nuova istanza del modal
		const modalInstance = new ModalConstructor({
			el: containerElement,
			propsData,
		});

		// Ritorna interfaccia pubblica usando any cast per compatibilità
		const modalInstanceAny = modalInstance as any;
		return {
			close: modalInstanceAny.close,
			startLoading: modalInstanceAny.startLoading,
			stopLoading: modalInstanceAny.stopLoading,
			getData: modalInstanceAny.getData,
		};
	} catch (error) {
		console.error('❌ Modal Factory: Failed to create modal instance:', error);
		throw error;
	}
}

/**
 * Servizio programmatico per gestione modal
 *
 * Implementa l'interfaccia principale per l'apertura di modal dinamici.
 * Fornisce API semplificata e type-safe mantenendo la retrocompatibilità
 * con il codice esistente.
 */
const ModalProgrammatic: ModalProgrammaticService = {
	/**
	 * Apre un nuovo modal con il componente specificato
	 *
	 * @param title - Titolo del modal
	 * @param body - Componente Vue da renderizzare nel modal
	 * @param bodyProps - Props da passare al componente (default: {})
	 * @param onConfirm - Callback eseguito alla conferma (default: noop)
	 * @param onCancel - Callback eseguito alla cancellazione (default: noop)
	 * @param confirmButton - Se mostrare il bottone conferma (default: true)
	 * @param cancelButtonText - Testo del bottone cancellazione (default: 'Annulla')
	 * @returns Istanza modal con metodi di controllo
	 *
	 * @example
	 * ### Uso Base
	 * ```typescript
	 * const modal = this.$modal.open(
	 *   'Modifica Utente',
	 *   UserEditForm,
	 *   { userId: 123 },
	 *   async ({ data }) => {
	 *     await userService.update(data);
	 *     this.$notification.success('Utente modificato!');
	 *   }
	 * );
	 * ```
	 *
	 * @example
	 * ### Uso Avanzato con Loading
	 * ```typescript
	 * const modal = this.$modal.open('Salva Dati', MyForm, props, this.onSave);
	 *
	 * async onSave({ data }) {
	 *   modal.startLoading();
	 *   try {
	 *     await myService.save(data);
	 *     modal.close();
	 *     this.refreshTable();
	 *   } catch (error) {
	 console.error('❌ ERROR index.error:', error);
	 *     modal.stopLoading(); // Lascia aperto per retry
	 *     this.$notification.error('Errore nel salvataggio');
	 *   }
	 * }
	 * ```
	 */
	open(
		title: string,
		body: any, // Vue Component
		bodyProps: Record<string, any> = {},
		onConfirm: ModalCallback = () => {
			/* noop */
		},
		onCancel: ModalCallback = () => {
			/* noop */
		},
		confirmButton: boolean = true,
		cancelButtonText: string = 'Annulla',
	): ModalInstance {
		const modalProps: ModalProps = {
			title,
			body,
			bodyProps,
			onConfirm,
			onCancel,
			confirmButton,
			cancelButtonText,
		};

		return modal(modalProps);
	},
};

/**
 * Plugin Vue per il sistema Modal
 *
 * Installa il servizio modal globalmente su Vue.prototype,
 * rendendolo accessibile come this.$modal in tutti i componenti.
 */
const ModalPlugin = {
	/**
	 * Funzione di installazione del plugin Vue
	 *
	 * @param Vue - Costruttore Vue
	 */
	install(Vue: VueConstructor): void {
		try {
			// Registra il servizio modal globalmente
			Vue.prototype.$modal = ModalProgrammatic;
		} catch (error) {
			console.error('❌ Modal Plugin: Failed to install plugin:', error);
			throw error;
		}
	},
} as { install: PluginFunction<any> };

// Export per uso esterno
export default ModalPlugin;
export { ModalProgrammatic };

/**
 * Type augmentation per Vue - aggiunge $modal al prototype
 *
 * Questo permette TypeScript di riconoscere this.$modal nei componenti Vue
 */
declare module 'vue/types/vue' {
	interface Vue {
		$modal: ModalProgrammaticService;
	}
}
